import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { DsButtonKind } from '@frontend/ui/button';
import { SessionStoreService } from '@frontend/vanilla/core';

import { EpcotConfigService } from '../../../common/epcot-config.service';
import { MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import {
    IFilterConstraint,
    MyBetsFilterConstraint,
    MyBetsMigrationFilterContstraint,
    MyBetsStateFilterContstraint,
} from '../../models/my-bets-filter-constraints';
import { IMyBetsListRequest } from '../../services/my-bets-api.service';

@Component({
    selector: 'ms-my-bets-filter-button',
    templateUrl: './my-bets-filter-button.html',
    styleUrl: 'my-bets-filter-button.scss',
})
export class MyBetsFilterButtonComponent implements OnInit {
    @Input() toggleResponsiveFilter: boolean;
    @Input() filterConstraint: MyBetsFilterConstraint;
    @Input() myBetsListContext: MyBetsListContext;
    @Input() betslipsCount: number;

    @Output() filterButtonClicked = new EventEmitter<boolean>();
    @Output() printButtonClicked = new EventEmitter<boolean>();
    @Output() constraintRemoved = new EventEmitter<IFilterConstraint<IMyBetsListRequest>>();

    private stateFilterConstraintSessionStorageKey = 'stateConstraint';
    private dateRangeFilterConstraintSessionStorageKey = 'dateRangeConstraint';
    isFiltered = false;
    isEpcotEnabled: boolean;
    readonly buttonThemeKind: ThemeOptions<DsButtonKind> = {
        'mgm-4': 'secondary',
    };

    constructor(
        public sitecore: Sitecore,
        private sessionStorage: SessionStoreService,
        private epcotConfigService: EpcotConfigService,
    ) {}

    ngOnInit(): void {
        this.isFiltered = this.getIsFiltered();
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    onConstraintIsRemoved(constraint: IFilterConstraint<IMyBetsListRequest>): void {
        this.constraintRemoved.emit(constraint);

        if (constraint instanceof MyBetsStateFilterContstraint) {
            this.sessionStorage.remove(this.stateFilterConstraintSessionStorageKey);
        } else {
            this.sessionStorage.remove(this.dateRangeFilterConstraintSessionStorageKey);
        }
    }

    onFilterBtnClicked(): void {
        this.filterButtonClicked.emit();
    }

    onPrintBtnClicked(): void {
        this.printButtonClicked.emit();
    }

    private getIsFiltered(): boolean {
        const onlyMigrationConstraint = this.filterConstraint.constraints.every((c) => this.isMigrationConstraint(c));

        return this.filterConstraint.constraints.length > 0 && !onlyMigrationConstraint && !!this.myBetsListContext.showFilter;
    }

    isMigrationConstraint(constraint: IFilterConstraint<IMyBetsListRequest>): boolean {
        return constraint instanceof MyBetsMigrationFilterContstraint;
    }

    get showFilterButton(): boolean {
        return !!this.myBetsListContext.showFilter;
    }

    get hasValidExportIconType(): boolean {
        return !!(
            this.myBetsListContext.listType === BetslipListType.Settled &&
            (this.myBetsListContext.showDownloadButton || this.myBetsListContext.showPrintButton)
        );
    }

    get getIconClass(): string {
        const { showDownloadButton, showPrintButton } = this.myBetsListContext;

        return showDownloadButton ? 'theme-download' : showPrintButton ? 'theme-print' : '';
    }

    get showDownloadIcon(): boolean {
        return !!this.myBetsListContext.showDownloadButton;
    }

    get printButtonTitle(): string {
        return this.sitecore.myBets_Print?.PrintButtonTitle;
    }

    get showMigrationTabs(): boolean | undefined {
        return this.myBetsListContext.showMigrationTabs;
    }

    get showConstraints(): boolean {
        return this.filterConstraint.constraints.length > 0 && this.showFilterButton;
    }

    get isSettled(): boolean {
        return this.myBetsListContext.listType === BetslipListType.Settled;
    }
}
